import api from './api';

export interface Hospital {
  id: number;
  nombre: string;
  direccion?: string;
  telefono?: string;
  whatsapp?: string;
  email?: string;
  contacto?: string;
  notas?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Especialidad {
  id: number;
  nombre: string;
  descripcion?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TipoCirugia {
  id: number;
  nombre: string;
  descripcion?: string;
  especialidadId?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ColaboradorCirugia {
  id: number;
  nombre: string;
  apellidos: string;
  tipo: string;
  especialidadId?: number;
  cedulaProfesional?: string;
  telefono?: string;
  email?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CirugiaColaborador {
  id: number;
  cirugiaId: number;
  colaboradorId: number;
  rolEnCirugia: string;
  confirmado: boolean;
  fechaConfirmacion?: string;
  notas?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Cirugia {
  id: number;
  pacienteId: number;
  hospitalId: number;
  tipoCirugiaId: number;
  fechaProgramada: string;
  fechaRealizada?: string;
  horaInicio?: string;
  horaFin?: string;
  padecimiento: string;
  salaQuirurgica?: string;
  estado: string;
  tipoPago: string;
  datosSeguro?: string;
  diagnosticoPreoperatorio?: string;
  diagnosticoPostoperatorio?: string;
  notasPreoperatorias?: string;
  notasPostoperatorias?: string;
  complicaciones?: string;
  observaciones?: string;
  duracionEstimada?: number;
  duracionReal?: number;
  tipoAnestesia?: string;
  riesgoQuirurgico?: string;
  requiereUci: boolean;
  diasHospitalizacion?: number;
  numeroExpedienteHospital?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  colaboradores?: CirugiaColaborador[];
  documentos?: any[];
}

export interface CreateCirugiaDto {
  pacienteId: number;
  hospitalId: number;
  tipoCirugiaId: number;
  fechaProgramada: string;
  horaInicio?: string;
  padecimiento: string;
  salaQuirurgica?: string;
  estado: string;
  tipoPago: string;
  datosSeguro?: string;
  diagnosticoPreoperatorio?: string;
  notasPreoperatorias?: string;
  observaciones?: string;
  duracionEstimada?: number;
  tipoAnestesia?: string;
  riesgoQuirurgico?: string;
  requiereUci?: boolean;
  diasHospitalizacion?: number;
  numeroExpedienteHospital?: string;
  colaboradores?: Array<{
    colaboradorId: number;
    rolEnCirugia: string;
    confirmado?: boolean;
    notas?: string;
  }>;
}

// Hospitales
export const getHospitales = async (params?: {
  skip?: number;
  take?: number;
  search?: string;
  isActive?: boolean;
}) => {
  const queryParams = new URLSearchParams();
  if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());
  if (params?.take !== undefined) queryParams.append('take', params.take.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());

  const response = await api.get(`/hospitales?${queryParams.toString()}`);
  return response.data;
};

export const getHospital = async (id: number) => {
  const response = await api.get(`/hospitales/${id}`);
  const payload = response?.data;
  if (payload?.data) {
    return payload.data;
  }
  if (payload?.data?.data) {
    return payload.data.data;
  }
  return payload;
};

export const createHospital = async (data: Partial<Hospital>) => {
  const response = await api.post('/hospitales', data);
  return response.data;
};

export const updateHospital = async (id: number, data: Partial<Hospital>) => {
  const response = await api.patch(`/hospitales/${id}`, data);
  return response.data;
};

export const deleteHospital = async (id: number) => {
  const response = await api.delete(`/hospitales/${id}`);
  return response.data;
};

// Especialidades
export const getEspecialidades = async (params?: {
  skip?: number;
  take?: number;
  search?: string;
  isActive?: boolean;
}) => {
  const queryParams = new URLSearchParams();
  if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());
  if (params?.take !== undefined) queryParams.append('take', params.take.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());

  const response = await api.get(`/especialidades?${queryParams.toString()}`);
  return response.data;
};

export const createEspecialidad = async (data: Partial<Especialidad>) => {
  const response = await api.post('/especialidades', data);
  return response.data;
};

// Tipos de Cirugía
export const getTiposCirugia = async (params?: {
  skip?: number;
  take?: number;
  search?: string;
  isActive?: boolean;
  especialidadId?: number;
}) => {
  const queryParams = new URLSearchParams();
  if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());
  if (params?.take !== undefined) queryParams.append('take', params.take.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
  if (params?.especialidadId) queryParams.append('especialidadId', params.especialidadId.toString());

  const response = await api.get(`/tipos-cirugia?${queryParams.toString()}`);
  return response.data;
};

export const createTipoCirugia = async (data: Partial<TipoCirugia>) => {
  const response = await api.post('/tipos-cirugia', data);
  return response.data;
};

export const getTipoCirugia = async (id: number) => {
  const response = await api.get(`/tipos-cirugia/${id}`);
  const payload = response?.data;
  if (payload?.data) {
    return payload.data;
  }
  if (payload?.data?.data) {
    return payload.data.data;
  }
  return payload;
};

// Colaboradores
export const getColaboradores = async (params?: {
  skip?: number;
  take?: number;
  search?: string;
  isActive?: boolean;
  tipo?: string;
  especialidadId?: number;
}) => {
  const queryParams = new URLSearchParams();
  if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());
  if (params?.take !== undefined) queryParams.append('take', params.take.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
  if (params?.tipo) queryParams.append('tipo', params.tipo);
  if (params?.especialidadId) queryParams.append('especialidadId', params.especialidadId.toString());

  const response = await api.get(`/colaboradores-cirugia?${queryParams.toString()}`);
  return response.data;
};

export const createColaborador = async (data: Partial<ColaboradorCirugia>) => {
  const response = await api.post('/colaboradores-cirugia', data);
  return response.data;
};

// Cirugías
export const getCirugias = async (params?: {
  skip?: number;
  take?: number;
  search?: string;
  isActive?: boolean;
  estado?: string;
  padecimiento?: string;
  pacienteId?: number;
  hospitalId?: number;
  tipoCirugiaId?: number;
  colaboradorId?: number;
  sala?: string;
  fechaDesde?: string;
  fechaHasta?: string;
}) => {
  const queryParams = new URLSearchParams();
  if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());
  if (params?.take !== undefined) queryParams.append('take', params.take.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
  if (params?.estado) queryParams.append('estado', params.estado);
  if (params?.padecimiento) queryParams.append('padecimiento', params.padecimiento);
  if (params?.pacienteId) queryParams.append('pacienteId', params.pacienteId.toString());
  if (params?.hospitalId) queryParams.append('hospitalId', params.hospitalId.toString());
  if (params?.tipoCirugiaId) queryParams.append('tipoCirugiaId', params.tipoCirugiaId.toString());
  if (params?.colaboradorId) queryParams.append('colaboradorId', params.colaboradorId.toString());
  if (params?.sala) queryParams.append('sala', params.sala);
  if (params?.fechaDesde) queryParams.append('fechaDesde', params.fechaDesde);
  if (params?.fechaHasta) queryParams.append('fechaHasta', params.fechaHasta);

  const response = await api.get(`/cirugias?${queryParams.toString()}`);
  return response.data;
};

export const getCirugia = async (id: number) => {
  const response = await api.get(`/cirugias/${id}`);
  return response.data;
};

export const getCirugiasPendientes = async () => {
  const response = await api.get('/cirugias/pendientes');
  return response.data;
};

export const getCirugiasByPaciente = async (pacienteId: number) => {
  const response = await api.get(`/cirugias/paciente/${pacienteId}`);
  return response.data;
};

export const getCirugiasByFecha = async (fecha: string) => {
  const response = await api.get(`/cirugias/fecha/${fecha}`);
  return response.data;
};

export const getCirugiasByMes = async (year: number, month: number) => {
  const response = await api.get(`/cirugias/mes/${year}/${month}`);
  return response.data;
};

export const createCirugia = async (data: CreateCirugiaDto) => {
  const response = await api.post('/cirugias', data);
  return response.data;
};

export const updateCirugia = async (id: number, data: Partial<Cirugia>) => {
  const response = await api.patch(`/cirugias/${id}`, data);
  return response.data;
};

export const deleteCirugia = async (id: number) => {
  const response = await api.delete(`/cirugias/${id}`);
  return response.data;
};

// Colaboradores de Cirugía
export const addColaboradorToCirugia = async (
  cirugiaId: number,
  data: {
    colaboradorId: number;
    rolEnCirugia: string;
    confirmado?: boolean;
    notas?: string;
  }
) => {
  const response = await api.post(`/cirugias/${cirugiaId}/colaboradores`, data);
  return response.data;
};

export const getColaboradoresDeCirugia = async (cirugiaId: number) => {
  const response = await api.get(`/cirugias/${cirugiaId}/colaboradores`);
  const payload = response?.data;
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  if (Array.isArray(payload?.data?.data)) {
    return payload.data.data;
  }
  return [];
};

export const updateColaboradorDeCirugia = async (
  cirugiaId: number,
  colaboradorId: number,
  data: {
    rolEnCirugia?: string;
    confirmado?: boolean;
    notas?: string;
  }
) => {
  const response = await api.patch(`/cirugias/${cirugiaId}/colaboradores/${colaboradorId}`, data);
  return response.data;
};

export const removeColaboradorDeCirugia = async (cirugiaId: number, colaboradorId: number) => {
  const response = await api.delete(`/cirugias/${cirugiaId}/colaboradores/${colaboradorId}`);
  return response.data;
};

export default {
  // Hospitales
  getHospitales,
  getHospital,
  createHospital,
  updateHospital,
  deleteHospital,
  // Especialidades
  getEspecialidades,
  createEspecialidad,
  // Tipos de Cirugía
  getTiposCirugia,
  createTipoCirugia,
  // Colaboradores
  getColaboradores,
  createColaborador,
  // Cirugías
  getCirugias,
  getCirugia,
  getCirugiasPendientes,
  getCirugiasByPaciente,
  getCirugiasByFecha,
  getCirugiasByMes,
  createCirugia,
  updateCirugia,
  deleteCirugia,
  // Colaboradores de Cirugía
  addColaboradorToCirugia,
  getColaboradoresDeCirugia,
  updateColaboradorDeCirugia,
  removeColaboradorDeCirugia,
};
